// The native-maps kit's icon images on a MapLibre web map (docs/NATIVE-MAPS-CONTRACT.md §1). The
// PopCart styles name icons that are not in any sprite: `poi-<PlaceCategory>`, `popcart-<line>` and
// `oneway`. A map that doesn't register them draws no place icons or one-way arrows and logs a
// missing-image warning per icon. Pure (the map and the PNG URLs come in): node --test runs it.

/** Every icon the kit ships (packages/ui/src/map/icons), in the kit's order. */
export const KIT_ICON_IDS = Object.freeze([
  'poi-food', 'poi-cafe', 'poi-shop', 'poi-grocery', 'poi-health', 'poi-school', 'poi-worship',
  'poi-transit', 'poi-bank', 'poi-fuel', 'poi-gov', 'poi-park', 'poi-landmark',
  'popcart-fresh', 'popcart-grill', 'popcart-mart', 'popcart-express', 'oneway',
]);

// The back office loads the 2x PNGs: crisp on retina, and MapLibre draws them at their 1x size.
export const KIT_PIXEL_RATIO = 2;

/**
 * @typedef {{
 *   hasImage(id: string): boolean;
 *   addImage(id: string, image: any, opts?: { pixelRatio?: number }): unknown;
 *   loadImage(url: string): Promise<{ data: any }>;
 *   setMissingStyleImageResolver?: (resolver: ((id: string) => void | Promise<void>) | null) => unknown;
 * }} IconMap
 */

/**
 * Register the kit icons on `map`. With MapLibre's missing-image resolver (v6) each icon loads the
 * first time a layer asks for it and MapLibre waits for it, so nothing warns and unused icons cost
 * nothing; without the resolver, every icon is loaded up front. An icon already on the map is left
 * alone, an id the kit doesn't have is ignored (MapLibre reports it as usual), and a PNG that fails
 * to load is retried on the next ask. Returns `load(id)`, for a caller that wants one now.
 * @param {IconMap} map
 * @param {Record<string, string | undefined>} urls icon id → PNG URL (the 2x file)
 * @param {{ pixelRatio?: number }} [opts]
 * @returns {(id: string) => Promise<void>}
 */
export function registerKitIcons(map, urls, { pixelRatio = KIT_PIXEL_RATIO } = {}) {
  /** @type {Map<string, Promise<void>>} */
  const inflight = new Map();
  /** @param {string} id */
  const load = (id) => {
    const url = urls[id];
    if (!url || map.hasImage(id)) return Promise.resolve();
    let p = inflight.get(id);
    if (!p) {
      p = map
        .loadImage(url)
        .then(({ data }) => {
          if (!map.hasImage(id)) map.addImage(id, data, { pixelRatio });
        })
        .catch(() => undefined)
        .finally(() => inflight.delete(id));
      inflight.set(id, p);
    }
    return p;
  };
  if (typeof map.setMissingStyleImageResolver === 'function') map.setMissingStyleImageResolver((id) => load(id));
  else for (const id of Object.keys(urls)) void load(id);
  return load;
}
